import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {TabContainer, Tab} from 'ui-library/TabContainer';

import {PlateMapType} from '../annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateMapDisplayLayerSidebar, {
  ConcentrationLayersProps,
  MetadataLayersProps,
  PickableLayersProps,
} from '../display-layer-controls/PlateMapDisplayLayerSidebar';
import {PlateRecord} from '../plate-record/PlateRecordConstants';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  DisplayLayerOptionSection,
  PlateMap,
  PlateMapDisplayLayerAttribute,
} from '../PlateMapConstants';
import {ValidationErrorsByLayerAndLevel} from '../validation/ValidationTypes';
import {CollapsibleSidebar} from './CollapsibleSidebar';
import {PlateRecordMetadata} from './PlateRecordMetadata';

interface PlateMapSidebarProps {
  plateRecord?: PlateRecord;

  /* Plate props */
  plateMap: PlateMap;
  selectedAttributeKey: string;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  assignmentGroups: {[attrKey: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>}};
  disabledSections: Array<DisplayLayerOptionSection>;
  /* Sections to be hidden entirely, rather than shown but disabled (as above). */
  hiddenSections?: Array<DisplayLayerOptionSection>;
  groupedValidationErrors: ValidationErrorsByLayerAndLevel;
  wellSchemaLayersProps: PickableLayersProps;
  plateMapType: PlateMapType;
  parameterLayersProps?: PickableLayersProps;
  concentrationLayersProps?: ConcentrationLayersProps;
  metadataLayersProps?: MetadataLayersProps;
  additionalContent?: JSX.Element;
}

/**
 * Left sidebar for displaying metadata and display layers through a tabbed interface.
 * Will only show metadata tab if there are schema field values.
 */
export function PlateMapSidebar(props: PlateMapSidebarProps): JSX.Element {
  const {plateRecord, ...plateProps} = props;
  const [activeTab, setActiveTab] = useState('plate');

  const hasPlateRecordSchemaFields = (plateRecord?.schemaFieldValues ?? []).length > 0;

  const content =
    plateRecord == null || !hasPlateRecordSchemaFields ? (
      <div {...getTestProps({component: 'PlateMapDisplayLayerSidebar', element: 'root'})}>
        <PlateMapDisplayLayerSidebar {...plateProps} />
      </div>
    ) : (
      <TabContainer
        activeTab={activeTab}
        onRequestTabChange={setActiveTab}
        className={css(styles.tabContainer)}
        paneClassName={css(styles.pane)}
      >
        <Tab tabKey="plate" title="PLATE">
          <div {...getTestProps({component: 'PlateMapDisplayLayerSidebar', element: 'root'})}>
            <PlateMapDisplayLayerSidebar {...plateProps} />
          </div>
        </Tab>

        <Tab tabKey="metadata" title="METADATA">
          <div {...getTestProps({component: 'PlateMapMetaData', element: 'root'})}>
            {plateRecord && <PlateRecordMetadata schemaFieldValues={plateRecord.schemaFieldValues} />}
          </div>
        </Tab>
      </TabContainer>
    );

  return <CollapsibleSidebar component="PlateMapSidebar">{content}</CollapsibleSidebar>;
}

const styles = StyleSheet.create({
  pane: {
    height: 'calc(100% - 7px)',
    marginTop: 0,
  },
  tabContainer: {
    height: '100%',
  },
});
